import {
  FARM_AVATAR_ANIMAL_POOL,
  FARM_AVATAR_ASSETS,
  FARM_AVATAR_COLORS,
  FARM_AVATAR_TYPES,
} from "../assets/farmAvatars";

export const APPROVED_FALLBACK_AVATARS = FARM_AVATAR_ANIMAL_POOL;
export { FARM_AVATAR_ANIMAL_POOL, FARM_AVATAR_ASSETS, FARM_AVATAR_COLORS, FARM_AVATAR_TYPES };

const ANIMAL_ALIASES = {
  rooster: "chicken",
  hen: "chicken",
  bunny: "rabbit",
};

export const getStableProfileAvatarHash = (value = "") => {
  const source = String(value || "profile");
  let hash = 0;

  for (let index = 0; index < source.length; index += 1) {
    hash = (hash * 31 + source.charCodeAt(index)) >>> 0;
  }

  return hash;
};

export const getProfileDisplayName = (profile = {}) =>
  String(
    profile.name ||
      profile.displayName ||
      profile.authorName ||
      profile.email ||
      "Community member"
  ).trim() || "Community member";

export const getProfileInitials = (profile = {}) => {
  const name = getProfileDisplayName(profile);
  const words = name.split(/\s+/).filter(Boolean);
  const initials = words.length > 1
    ? `${words[0][0] || ""}${words[1][0] || ""}`
    : name.slice(0, 1);

  return initials.toUpperCase() || "U";
};

export const getProfileStableId = (profile = {}) =>
  String(
    profile.uid ||
      profile.id ||
      profile.authorId ||
      profile.userId ||
      profile.email ||
      getProfileDisplayName(profile)
  );

export const normalizeProfileAnimal = (value) => {
  const cleanValue = String(value || "").trim().toLowerCase();
  const normalized = ANIMAL_ALIASES[cleanValue] || cleanValue;
  return APPROVED_FALLBACK_AVATARS.includes(normalized) ? normalized : "";
};

export const hasUploadedProfilePhoto = (profile = {}) =>
  Boolean(profile.profilePicture || profile.photoURL || profile.photo);

export const hasExplicitFarmAnimalSelection = (profile = {}) => {
  const selectedAnimal = normalizeProfileAnimal(profile.farmAnimal);
  if (!selectedAnimal) return false;
  if (profile.farmAnimalSelectionExplicit === true) return true;
  if (profile.farmAnimalSelectionExplicit === false) return false;

  return selectedAnimal !== "goat";
};

export const isBarnAppropriateProfile = (profile = {}) => {
  const roles = Array.isArray(profile.roles)
    ? profile.roles
    : [profile.role, profile.type].filter(Boolean);
  const isCommunityOrWorkerOnly =
    roles.length > 0 &&
    roles.every((role) => role === "community" || role === "day_worker");

  const businessSignals = [
    profile.businessName,
    profile.storeName,
    profile.farmStandName,
    profile.standName,
    profile.publicBusinessName,
    profile.storefrontEnabled === true,
    profile.publicSlug,
    profile.storefrontSlug,
    profile.avatarProfileType === "business",
    profile.avatarProfileType === "seller",
    profile.avatarProfileType === "farm_stand",
  ];

  return !isCommunityOrWorkerOnly && businessSignals.some(Boolean);
};

export const getDeterministicProfileAvatarAnimal = (profile = {}) => {
  const stableId = getProfileStableId(profile);
  const index =
    getStableProfileAvatarHash(`${stableId}:animal`) %
    APPROVED_FALLBACK_AVATARS.length;

  return APPROVED_FALLBACK_AVATARS[index];
};

export const getProfileAvatarAnimal = (profile = {}) => {
  const selectedAnimal = normalizeProfileAnimal(profile.farmAnimal);
  if (selectedAnimal && hasExplicitFarmAnimalSelection(profile)) return selectedAnimal;
  if (isBarnAppropriateProfile(profile)) return "barn";

  return getDeterministicProfileAvatarAnimal(profile);
};

export const getProfileAvatarColor = (profile = {}) => {
  const stableId = getProfileStableId(profile);
  const index =
    getStableProfileAvatarHash(`${stableId}:color`) %
    FARM_AVATAR_COLORS.length;

  return FARM_AVATAR_COLORS[index];
};

export const getProfileAvatarBackground = (profile = {}) =>
  getProfileAvatarColor(profile).className;

export const getProfileAvatarLabel = (profile = {}) => {
  const name = getProfileDisplayName(profile);
  const photo = profile.profilePicture || profile.photoURL || profile.photo || "";

  if (photo) return `Profile photo for ${name}.`;

  const animal = getProfileAvatarAnimal(profile);
  return `${animal.charAt(0).toUpperCase()}${animal.slice(1)} avatar for ${name}.`;
};

export const getProfileAvatar = (profile = {}) => {
  const photo = profile.profilePicture || profile.photoURL || profile.photo || "";

  if (photo) {
    return {
      type: "photo",
      photo,
      initials: getProfileInitials(profile),
      label: getProfileAvatarLabel(profile),
    };
  }

  const animal = getProfileAvatarAnimal(profile);
  const color = getProfileAvatarColor(profile);

  return {
    type: "fallback",
    animal,
    asset: FARM_AVATAR_ASSETS[animal],
    colorId: color.id,
    colorClassName: color.className,
    initials: getProfileInitials(profile),
    label: getProfileAvatarLabel(profile),
  };
};
